/* eslint-disable react/display-name */
import React from "react";
import { InputAdornment } from "@mui/material";
import { format, isValid } from "date-fns";
import * as Yup from "yup";
import { NumberFormat } from "../components/Form";
import { DATE_FORMAT, FINANCE_TYPE } from "../constants";
import { DirhamSymbol } from "../components/DirhamSymbol";
import { NAME_REGEX, DECIMAL_TWO_DIGIT_DECIMAL_REGEX, PERCENTAGE_REGEX } from "./validation";
import { getRequiredMessage, getInvalidMessage, getMinDateMessage } from "./getValidationMessage";

/**
 * Current date constants used for validation
 */
export const currentYear = format(new Date(), "yyyy");
export const currentMonth = format(new Date(), "MM");
export const currentDay = format(new Date(), "dd");

/**
 * Common initial values for finance forms
 */
export const commonInitialValues = {
  loanType: "",
  financeType: "",
  processingFee: "",
  interestRate: "",
  endUseMonitoring: [],
  upfrontPostPactPercentage: 0,
  machineryType: "",
  toBeNotarized: "",
  justificationApplicableThreshold: "",
  paymentTo: "",
  purchaseOrderProvided: "",
  ownerDetails: "",
  registrationDetails: "",
  modelYear: "",
  description: "",
  serialOrChasisNo: "",
  makeType: "",
  engineNo: "",
  equipmentLocation: "",
  otherspecification: "",
  insuranceUnderlyingAsset: "",
  assetValue: "",
  assetFinanceAmount: "",
  downPayment: "",
  analysisType: "",
  vendorName: "",
  vendorCode: "",
  invoiceNumber: "",
  invoiceDate: "",
  valuatorName: "",
  valuatorDate: ""
};

/**
 * Additional initial values for BusinessFinance
 */
export const businessFinanceInitialValues = {
  ...commonInitialValues,
  ntcAmount: 0,
  existingLoanTenor: "",
  noOfEmisOnExistingLoan: "",
  tessLoanAvailed: "",
  tessLoanOutstandingWithRakbank: 0,
  proofOfEndUseOption: "",
  purposeOfLoanDetail: ""
};

/**
 * Common Yup validation schema fields
 */
export const commonValidationFields = {
  loanType: Yup.string().required(getRequiredMessage("Type of Loan")),
  financeType: Yup.string().required(getRequiredMessage("Finance type")),
  processingFee: Yup.string().matches(PERCENTAGE_REGEX, "Processing fee should be 0-100"),
  interestRate: Yup.string().matches(PERCENTAGE_REGEX, "Interest rate should be 0-100"),
  machineryType: Yup.string().max(50, "Maximum 50 characters allowed"),
  endUseMonitoring: Yup.array(),
  toBeNotarized: Yup.string(),
  justificationApplicableThreshold: Yup.string().max(255, "Maximum 255 characters allowed"),
  paymentTo: Yup.string()
    .max(50, "Maximum 50 characters allowed")
    .matches(NAME_REGEX, getInvalidMessage("Payment to (Name)")),
  purchaseOrderProvided: Yup.string(),
  ownerDetails: Yup.string().max(50, "Maximum 50 characters allowed"),
  registrationDetails: Yup.string().max(50, "Maximum 50 characters allowed"),
  modelYear: Yup.date()
    .nullable()
    .min(new Date(1900, 0, 1), getMinDateMessage("model/year"))
    .max(new Date(currentYear, 11, 31), getInvalidMessage("model/year"))
    .typeError(getInvalidMessage("model/year")),
  description: Yup.string().max(50, "Maximum 50 characters allowed"),
  serialOrChasisNo: Yup.string().max(50, "Maximum 50 characters allowed"),
  makeType: Yup.string().max(50, "Maximum 50 characters allowed"),
  engineNo: Yup.string().max(50, "Maximum 50 characters allowed"),
  equipmentLocation: Yup.string().max(50, "Maximum 50 characters allowed"),
  otherspecification: Yup.string().max(50, "Maximum 50 characters allowed"),
  assetValue: Yup.string()
    .max(13, "Maximum 13 characters allowed")
    .matches(DECIMAL_TWO_DIGIT_DECIMAL_REGEX, getInvalidMessage("Asset Value")),
  assetFinanceAmount: Yup.string().max(13, "Maximum 13 characters allowed"),
  downPayment: Yup.string().max(13, "Maximum 13 characters allowed"),
  analysisType: Yup.string().max(50, "Maximum 50 characters allowed"),
  insuranceUnderlyingAsset: Yup.string().nullable(),
  vendorName: Yup.string()
    .max(80, "Maximum 80 characters allowed")
    .matches(NAME_REGEX, getInvalidMessage("Name")),
  vendorCode: Yup.string().max(50, "Maximum 50 characters allowed"),
  invoiceNumber: Yup.string().max(50, "Maximum 50 characters allowed"),
  invoiceDate: Yup.date()
    .nullable()
    .min(new Date(1900, 0, 1), getMinDateMessage("Invoice Date"))
    .typeError(getInvalidMessage("Invoice Date")),
  valuatorName: Yup.string()
    .max(80, "Maximum 80 characters allowed")
    .matches(NAME_REGEX, getInvalidMessage("Name")),
  valuatorDate: Yup.date()
    .nullable()
    .min(new Date(1900, 0, 1), getMinDateMessage("Valuation Date"))
    .max(new Date(currentYear, currentMonth - 1, currentDay), getInvalidMessage("Valuation Date"))
    .typeError(getInvalidMessage("Valuation Date")),
  upfrontPostPactPercentage: Yup.number().nullable()
};

/**
 * Creates validation schema for Asset-based Finance
 */
export const createAssetBasedFinanceValidationSchema = () => {
  return Yup.object(commonValidationFields);
};

/**
 * Creates validation schema for Business Finance with additional fields
 */
export const createBusinessFinanceValidationSchema = () => {
  return Yup.object({
    ...commonValidationFields,
    ntcAmount: Yup.number()
      .typeError(getInvalidMessage("NTC Amount"))
      .min(0, getInvalidMessage("NTC Amount must be non-negative")),
    processingFee: Yup.number()
      .typeError(getInvalidMessage("Processing fee"))
      .min(0, getInvalidMessage("Processing fee must be non-negative")),
    interestRate: Yup.number()
      .min(0, getInvalidMessage("Interest rate must be non-negative"))
      .typeError(getInvalidMessage("Interest rate")),
    existingLoanTenor: Yup.string().max(10, "Maximum 10 characters allowed"),
    noOfEmisOnExistingLoan: Yup.string().max(10, "Maximum 10 characters allowed"),
    tessLoanAvailed: Yup.string(),
    tessLoanOutstandingWithRakbank: Yup.number()
      .typeError(getInvalidMessage("TESS Loan outstanding with RAKBank"))
      .min(0, getInvalidMessage("TESS Loan outstanding with RAKBank must be non-negative")),
    proofOfEndUseOption: Yup.string(),
    assetDownPayment: Yup.string().when("financeType", {
      is: value => value === FINANCE_TYPE,
      then: Yup.string() // NOSONAR
        .required(getRequiredMessage("Downpayment"))
        .max(13, "Maximum 13 characters allowed")
    }),
    purposeOfLoanDetail: Yup.string()
      .required(getRequiredMessage("Purpose of loan in detail"))
      .max(500, "Maximum 500 characters allowed")
  });
};

/**
 * Creates validation schema for Trade & Working Capital Finance
 */
export const createTradeWorkingCapitalValidationSchema = () => {
  return Yup.object({
    ...commonValidationFields,
    assetDownPayment: Yup.string().when("financeType", {
      is: value => value === FINANCE_TYPE,
      then: Yup.string() // NOSONAR
        .required(getRequiredMessage("Downpayment"))
        .max(13, "Maximum 13 characters allowed")
    })
  });
};

/**
 * Formatted decimal number input component for currency fields
 */
export const FormatDecimalNumberInput = props => (
  <NumberFormat
    allowNegative={false}
    thousandSeparator={true}
    decimalSeparator={false}
    {...props}
  />
);

/**
 * Common input props for currency fields
 */
export const commonInputProps = {
  endAdornment: (
    <InputAdornment position="end">
      <DirhamSymbol />
    </InputAdornment>
  ),
  inputProps: { tabIndex: 0 },
  inputComponent: FormatDecimalNumberInput
};

/**
 * Common input props for percentage fields
 */
export const commonPercentageInputProps = {
  endAdornment: <InputAdornment position="end">%</InputAdornment>,
  inputProps: { tabIndex: 0 },
  inputComponent: props => <NumberFormat allowNegative={false} {...props} />
};

/**
 * NonNegativeNumberFormat - Number format component that disallows negative values
 */
export const NonNegativeNumberFormat = props => <NumberFormat allowNegative={false} {...props} />;

/**
 * Input props for non-negative number fields (BusinessFinance)
 */
export const nonNegativeInputProps = {
  inputProps: { tabIndex: 0 },
  inputComponent: props => <NumberFormat allowNegative={false} {...props} />
};

/**
 * Common input props for currency fields (SalesAssist variant)
 */
export const commonCurrencyInputProps = {
  endAdornment: (
    <InputAdornment position="end">
      <DirhamSymbol />
    </InputAdornment>
  ),
  inputProps: { tabIndex: 0 },
  inputComponent: FormatDecimalNumberInput
};

/**
 * Standard input props for text fields
 */
export const standardInputProps = {
  inputProps: { tabIndex: 0 }
};

/**
 * Date change handler for prospect documents
 * @param {*} _ - Unused parameter
 * @param {Date} value - The selected date value
 * @param {string} path - The path to update in the prospect document
 * @returns {Object|undefined} - Update object or undefined if date is invalid
 */
export const changeDateProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(value, DATE_FORMAT) };

/**
 * Create change handler for asset down payment field
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} updateRoProspect - Action creator for updating prospect
 * @returns {Function} Change handler function
 */
export const createDownPaymentChangeHandler = (dispatch, updateRoProspect) => (
  setFieldValue,
  event
) => {
  const { name, value } = event.target;
  if (event.target.value === "") {
    dispatch(
      updateRoProspect({
        "roProspect.financeInfo.machineryDetails.assetDownPayment": ""
      })
    );
  }
  setFieldValue(name, value);
};

/**
 * Common validation rules for basic finance fields
 */
export const getBasicFinanceValidationRules = () => ({
  loanType: Yup.string().required(getRequiredMessage("Type of Loan")),
  financeType: Yup.string().required(getRequiredMessage("Finance type")),
  endUseMonitoring: Yup.array(),
  upfrontPostPactPercentage: Yup.number().nullable()
});

/**
 * Validation rules for percentage fields
 */
export const getPercentageValidationRules = () => ({
  processingFee: Yup.string().matches(PERCENTAGE_REGEX, "Processing fee should be 0-100"),
  interestRate: Yup.string().matches(PERCENTAGE_REGEX, "Interest rate should be 0-100")
});

/**
 * Get common validation rules for machinery details
 */
export const getMachineryValidationRules = () => {
  const {
    machineryType,
    toBeNotarized,
    justificationApplicableThreshold,
    paymentTo,
    purchaseOrderProvided,
    ownerDetails,
    registrationDetails,
    modelYear,
    description,
    serialOrChasisNo,
    makeType,
    engineNo,
    equipmentLocation,
    otherspecification,
    assetValue,
    assetFinanceAmount,
    analysisType,
    insuranceUnderlyingAsset
  } = commonValidationFields;

  return {
    machineryType,
    toBeNotarized,
    justificationApplicableThreshold,
    paymentTo,
    purchaseOrderProvided,
    ownerDetails,
    registrationDetails,
    modelYear,
    description,
    serialOrChasisNo,
    makeType,
    engineNo,
    equipmentLocation,
    otherspecification,
    assetValue,
    assetFinanceAmount,
    analysisType,
    insuranceUnderlyingAsset
  };
};

/**
 * Get common validation rules for vendor information
 */
export const getVendorValidationRules = () => {
  const {
    vendorName,
    vendorCode,
    invoiceNumber,
    invoiceDate,
    valuatorName,
    valuatorDate
  } = commonValidationFields;

  return {
    vendorName,
    vendorCode,
    invoiceNumber,
    invoiceDate,
    valuatorName,
    valuatorDate
  };
};

/**
 * Get down payment validation rule
 * @param {string} financeType - The finance type to check against
 * @param {boolean} isRequired - Whether down payment is required
 * @returns {object} Yup validation schema for down payment
 */
export const getDownPaymentValidation = (financeType, isRequired = false) => {
  if (isRequired) {
    return Yup.string().when("financeType", {
      is: value => value === financeType,
      then: Yup.string() // NOSONAR
        .required(getRequiredMessage("Downpayment"))
        .max(13, "Maximum 13 characters allowed")
    });
  }
  return Yup.string().max(13, "Maximum 13 characters allowed");
};

/**
 * Create validation schema for finance forms with machinery details
 * @param {object} additionalRules - Additional validation rules to merge
 * @returns {object} Yup validation schema
 */
export const createFinanceValidationSchema = (additionalRules = {}) => {
  return Yup.object({
    ...getBasicFinanceValidationRules(),
    ...getPercentageValidationRules(),
    ...getMachineryValidationRules(),
    ...getVendorValidationRules(),
    ...additionalRules
  });
};

/**
 * Create initial values for finance forms
 * @param {object} additionalValues - Additional initial values to merge
 * @returns {object} Initial values object
 */
export const createFinanceInitialValues = (additionalValues = {}) => {
  return {
    loanType: "",
    financeType: "",
    endUseMonitoring: [],
    upfrontPostPactPercentage: null,
    processingFee: "",
    interestRate: "",
    ...additionalValues
  };
};

/**
 * Common prospect path for finance information
 */
export const FINANCE_PROSPECT_PATH = "roProspect.financeInfo";
